/**
 * Module dependencies.
 */

import { GetServerSideProps, NextPage } from 'next/types';
import { Loading } from 'src/components/core';
import { TechnologyProps } from 'src/types/technologies';
import { fetchTechnology } from 'src/api/technologies/fetch-technology';
import { media, units } from '@untile/react-components';
import { routeTemplate } from 'src/core/utils/routes';
import { routes } from 'src/core/routes';
import { theme } from 'styled-tools';
import { useRouter } from 'next/router';
import GalleryPage from 'src/components/pages/gallery';
import Metatags from 'src/components/core/metatags';
import get from 'lodash/get';
import pick from 'lodash/pick';
import size from 'lodash/size';
import styled from 'styled-components';
import useTechnology from 'src/api/technologies/use-technology';

/**
 * `Props` type.
 */

type Props = {
  initialData: TechnologyProps | null;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  min-height: 100vh;
  padding: ${theme('dimensions.navbarHeightMobile')}px 0 ${units(8)};
  position: relative;

  ${media.min('md')`
    padding: calc(${theme('dimensions.navbarHeight')}px + ${units(
    6
  )}) 0 ${units(18)};
  `}
`;

/**
 * `TechnologyDetailsGallery` page.
 */

const TechnologyDetailsGallery: NextPage<Props> = ({ initialData }) => {
  const router = useRouter();
  const { data, isLoading, isSuccess } = useTechnology({
    initialData,
    slug: get(router.query, 'slug') as string
  });

  return (
    <>
      <Metatags
        pageTitle={data.title}
        title={data.title}
      />

      <Wrapper>
        <Loading active={isLoading} />

        {isSuccess && (
          <GalleryPage
            pageType={'technology'}
            {...pick(data, ['gallery', 'slug', 'subtitle', 'title'])}
          />
        )}
      </Wrapper>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export const getServerSideProps: GetServerSideProps<Props> = async ({
  locale,
  params
}) => {
  const initialData = await fetchTechnology({
    locale,
    slug: params?.slug as string
  });

  if (!initialData) {
    return {
      notFound: true
    };
  }

  if (size(initialData.gallery) === 0) {
    return {
      redirect: {
        destination: routeTemplate(routes[locale].modelRangeDetails, {
          slug: params?.slug as string
        }),
        permanent: false
      }
    };
  }

  return {
    props: {
      hasMobileScrollDownIndicator: true,
      hasScrollDownIndicator: true,
      initialData
    }
  };
};

/**
 * Export `TechnologyDetailsGallery` page.
 */

export default TechnologyDetailsGallery;
